/**
 * LL Embroidery — dynamic checkout guard
 *
 * Shopify's dynamic checkout buttons ({{ form | payment_button }} → "Buy with
 * Shop" / "More payment options") go straight to checkout with the single
 * variant held by the product form. Two consequences for embroidery:
 *
 *   1. They never dispatch a `submit` event on the form, so the capture-phase
 *      listener in embroidery-form-interceptor.js never sees the click.
 *   2. Accelerated checkout cannot carry a second line item, and embroidery is
 *      a separate product (`embroidery-service`). So even a perfect property
 *      hand-off would ship the embroidery for free.
 *
 * While embroidery is selected we therefore mark the payment button `inert`
 * and cover it with a transparent veil. The veil takes the click, runs the
 * same combined add (main product + embroidery, linked by `_addonToken`) that
 * ADD TO CART uses, then sends the customer to /checkout.
 *
 * Note this routes through the cart, so anything already in the cart comes
 * along — unlike native accelerated checkout, which buys only this product.
 */
(function () {
  'use strict';

  var GUARD_CLASS = 'll-dc-guard';
  var VEIL_CLASS = 'll-dc-veil';
  var PAYMENT_BUTTON = '.shopify-payment-button';
  var PRODUCT_FORM = 'form[is="product-form"], form[action*="/cart/add"]';

  /**
   * Same rule embroidery-form-interceptor.js uses to decide whether to take
   * over a submit (assets/embroidery-form-interceptor.js:281): an addon
   * checkbox is checked, or embroidery text has been filled in. Both the
   * checkbox and the property inputs live outside the <form> and are attached
   * to it via the `form` attribute, so each lookup needs the external pass.
   */
  function readEmbroideryState(form) {
    var formId = form.getAttribute('id');

    var checked = form.querySelector('.ll-embroidery-checkbox-input:checked, [data-addon-checkbox-input]:checked');
    if (!checked && formId) {
      checked = document.querySelector(
        '.ll-embroidery-checkbox-input[form="' + formId + '"]:checked, [data-addon-checkbox-input][form="' + formId + '"]:checked'
      );
    }

    function value(name) {
      var input = form.querySelector('input[name="' + name + '"]');
      if (!input && formId) {
        input = document.querySelector('input[name="' + name + '"][form="' + formId + '"]');
      }
      return input && input.value ? input.value.trim() : '';
    }

    var text = value('properties[Embroidery Text]');

    return {
      selected: !!checked || text !== '',
      complete: text !== '' && value('properties[Embroidery Font]') !== '' && value('properties[Embroidery Color]') !== ''
    };
  }

  /**
   * Embroidery containers live outside the <form> and bind to it through the
   * `form` attribute on their checkbox, so match on that rather than on
   * containment.
   */
  function findContainer(form) {
    var formId = form.getAttribute('id');
    var containers = document.querySelectorAll('.ll-embroidery-container[data-drawer-id]');

    for (var i = 0; i < containers.length; i++) {
      var checkbox = containers[i].querySelector('[data-addon-checkbox-input]');
      if (formId && checkbox && checkbox.getAttribute('form') !== formId) continue;
      return containers[i];
    }
    return null;
  }

  function openDrawerFor(form) {
    var container = findContainer(form);
    if (!container) return false;

    var drawer = document.getElementById(container.dataset.drawerId);
    if (!drawer || typeof drawer.show !== 'function') return false;

    drawer.show();
    return true;
  }

  function checkoutUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'checkout';
  }

  async function onVeilClick(event, form, veil) {
    event.preventDefault();
    event.stopPropagation();

    if (veil.dataset.busy === 'true') return;

    var state = readEmbroideryState(form);

    // Nothing selected — the veil should not have been visible. Re-sync and
    // let the customer hit the real button.
    if (!state.selected) {
      sync();
      return;
    }

    // Selected but half-filled: send them back to finish instead of eating
    // the click. Mirrors the interceptor's completeness gate (:332).
    if (!state.complete) {
      if (!openDrawerFor(form)) {
        console.error('[ll-embroidery] incomplete embroidery details and no drawer to open');
      }
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Same retry-wait the interceptor uses (:358-368) — the global is defined
    // at top level of a deferred script, so this is belt-and-braces.
    var attempts = 0;
    while (!window.addToCartEmbroidery && attempts < 50) {
      await new Promise(function (resolve) { setTimeout(resolve, 100); });
      attempts++;
    }

    if (!window.addToCartEmbroidery) {
      console.error('[ll-embroidery] window.addToCartEmbroidery unavailable, aborting dynamic checkout');
      return;
    }

    veil.dataset.busy = 'true';
    veil.classList.add('is-busy');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      // Returns a Response on success, or null when it cannot resolve the
      // product / embroidery variant (assets/ll-embroidery.js:442, :472).
      var response = await window.addToCartEmbroidery(form);
      if (!response || !response.ok) {
        throw new Error('cart/add.js rejected the combined embroidery payload');
      }

      window.location.href = checkoutUrl();
      // Deliberately stay "busy" — we are navigating away, and clearing it
      // would let a second click fire another add during the unload.
    } catch (error) {
      console.error('[ll-embroidery] dynamic checkout failed:', error);

      form.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: error.message || 'Failed to add products to cart' }
      }));

      veil.dataset.busy = 'false';
      veil.classList.remove('is-busy');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  /**
   * Wrap a payment button so the veil has a positioned container to fill.
   *
   * The veil has to be a *sibling* of `.shopify-payment-button`, not a child:
   * `inert` applies to the whole subtree, so a veil inside it would stop
   * receiving clicks. Shopify renders into `.shopify-payment-button` but does
   * not own the attributes of that container, so `inert` survives re-renders.
   *
   * Done once per button, as early as possible — moving a mounted button would
   * reload its Shop Pay iframe.
   */
  function wrap(paymentButton) {
    if (paymentButton.closest('.' + GUARD_CLASS)) return;

    var form = paymentButton.closest(PRODUCT_FORM);
    if (!form) return;

    // Products without an embroidery block keep their markup untouched.
    if (!findContainer(form)) return;

    var wrapper = document.createElement('div');
    wrapper.className = GUARD_CLASS;
    paymentButton.parentNode.insertBefore(wrapper, paymentButton);
    wrapper.appendChild(paymentButton);

    var veil = document.createElement('button');
    veil.type = 'button';
    veil.className = VEIL_CLASS;
    veil.dataset.busy = 'false';
    veil.setAttribute('aria-label', 'Checkout with embroidery');
    wrapper.appendChild(veil);

    veil.addEventListener('click', function (event) {
      onVeilClick(event, form, veil);
    });
  }

  function wrapAll() {
    var buttons = document.querySelectorAll(PAYMENT_BUTTON);
    for (var i = 0; i < buttons.length; i++) wrap(buttons[i]);
  }

  /**
   * Show/hide the veil to match embroidery state. `inert` is what actually
   * closes the keyboard path — without it a customer could Tab into the Shop
   * Pay iframe and press Enter, sailing past the veil.
   */
  function sync() {
    var wrappers = document.querySelectorAll('.' + GUARD_CLASS);

    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      var paymentButton = wrapper.querySelector(PAYMENT_BUTTON);
      var form = wrapper.closest(PRODUCT_FORM);
      if (!paymentButton || !form) continue;

      var active = readEmbroideryState(form).selected;
      wrapper.toggleAttribute('data-active', active);

      if (active) {
        paymentButton.setAttribute('inert', '');
      } else {
        paymentButton.removeAttribute('inert');
      }
    }
  }

  function init() {
    wrapAll();
    sync();

    // ll-embroidery.liquid flips the checkbox programmatically, which fires no
    // `change` event — it dispatches this instead.
    document.addEventListener('ll-embroidery:state-change', sync);

    // Direct user interaction with the checkbox, plus theme cart/variant churn.
    document.addEventListener('change', function (event) {
      if (event.target && event.target.matches && event.target.matches('.ll-embroidery-checkbox-input, [data-addon-checkbox-input]')) {
        sync();
      }
    });
    document.addEventListener('cart:refresh', sync);
    document.addEventListener('variant:change', sync);

    // Payment buttons can be re-rendered by the theme or by Shopify's own
    // script; pick up any that appear later. Coalesced to one pass per frame —
    // this observer sees every DOM change on the page, cart drawer included.
    if (window.MutationObserver) {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          wrapAll();
          sync();
        });
      }).observe(document.body, { childList: true, subtree: true });
    }

    // Backstop for any path that mutates the inputs without announcing it.
    setInterval(sync, 1500);
  }

  // Wrap at script-execution time (deferred, so the DOM is parsed) rather than
  // waiting for DOMContentLoaded — the earlier this runs, the more likely it
  // beats Shopify's async payment-button mount and avoids an iframe reload.
  wrapAll();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
